import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MODULE_KEYS, MODULE_LABEL, MODULE_HINT } from '../../auth/capabilities';
import { VpState } from '../../components/VelopulseShell';
import useLoad from '../../utils/useLoad';
import { vpApi, errorMessage } from '../../api/velopulse';

const emptyForm = {
  name: '',
  short_name: '',
  code: '',
  industry: '',
  admin_email: '',
  admin_name: '',
  admin_password: '',
  modules: [],
};

const toggleModule = (list, key) => (
  list.includes(key) ? list.filter((k) => k !== key) : [...list, key]
);

function ModulePicker({ modules, onChange }) {
  return (
    <fieldset className="vp-module-pick">
      <legend>產業功能 *（可複選，至少一項）</legend>
      <div className="vp-module-cards">
        {MODULE_KEYS.map((key) => {
          const on = modules.includes(key);
          return (
            <label key={key} className={`vp-module-card${on ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => onChange(toggleModule(modules, key))}
              />
              <span className="vp-module-card-title">{key === 'real_estate' ? '賣房' : '賣車'}</span>
              <span className="vp-module-card-sub">{MODULE_LABEL[key]}</span>
              <small>{MODULE_HINT[key]}</small>
            </label>
          );
        })}
      </div>
      <p className="vp-muted">
        賣房開通稅試算／調謄本／搜房；賣車開通車輛紀錄／名片王／號牌查詢。
        商機在有任一模組時可用，但目錄會依已開通模組過濾。
      </p>
    </fieldset>
  );
}

export default function PlatformCompaniesPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const { data, error, loading, reload } = useLoad(() => vpApi.listPlatformCompanies({ q }), [q]);
  const companies = Array.isArray(data) ? data : [];
  const [showCreate, setShowCreate] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setForm(emptyForm);
    setShowCreate(true);
  };

  const openEdit = (c, e) => {
    e?.stopPropagation?.();
    setEdit(c);
    setForm({
      name: c.name || '',
      short_name: c.short_name || '',
      code: c.code || '',
      industry: c.industry || '',
      modules: Array.isArray(c.modules) ? [...c.modules] : [],
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('請輸入公司名稱'); return; }
    if (!form.modules.length) { toast.error('請至少選擇賣房或賣車其中一項'); return; }
    const labels = form.modules.map((k) => MODULE_LABEL[k] || k).join('、');
    const ok = window.confirm(`確定建立公司「${form.name.trim()}」並開通：${labels}？`);
    if (!ok) return;
    setSaving(true);
    try {
      const created = await vpApi.createPlatformCompany({
        name: form.name.trim(),
        short_name: form.short_name.trim(),
        code: form.code.trim(),
        industry: form.industry.trim(),
        admin_email: form.admin_email.trim() || undefined,
        admin_name: form.admin_name.trim() || undefined,
        admin_password: form.admin_password || undefined,
        modules: form.modules,
      });
      toast.success(created?.admin_created ? '公司已建立，並開好管理員帳號' : '公司已建立');
      setShowCreate(false);
      reload();
      if (created?.id) navigate(`/platform/companies/${created.id}`);
    } catch (err) {
      toast.error(errorMessage(err, '無法建立公司'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!edit) return;
    if (!form.name.trim()) { toast.error('請輸入公司名稱'); return; }
    if (!form.modules.length) { toast.error('請至少選擇賣房或賣車其中一項'); return; }
    const labels = form.modules.map((k) => MODULE_LABEL[k] || k).join('、');
    const ok = window.confirm(`確定更新「${edit.name}」的資料與模組（${labels}）？\n\n公司管理員只能查看模組，不能自己開關。`);
    if (!ok) return;
    setSaving(true);
    try {
      await vpApi.updatePlatformCompany(edit.id, {
        name: form.name.trim(),
        short_name: form.short_name.trim(),
        code: form.code.trim(),
        industry: form.industry.trim(),
        modules: form.modules,
      });
      toast.success('公司資料已更新');
      setEdit(null);
      reload();
    } catch (err) {
      toast.error(errorMessage(err, '無法更新公司'));
    } finally {
      setSaving(false);
    }
  };

  const handleActive = async (c, e) => {
    e?.stopPropagation?.();
    const next = c.is_active === false;
    const ok = window.confirm(next
      ? `確定啟用「${c.name}」？成員將可以再次進入這家公司。`
      : `確定停用「${c.name}」？成員將無法再進入這家公司，既有資料不會刪除。`);
    if (!ok) return;
    try {
      await vpApi.updatePlatformCompany(c.id, { is_active: next });
      toast.success(next ? '公司已啟用' : '公司已停用');
      reload();
    } catch (err) {
      toast.error(errorMessage(err, '無法變更公司狀態'));
    }
  };

  if (loading && !companies.length) return <div className="vp-content"><VpState loading /></div>;

  return (
    <div className="vp-content" style={{ maxWidth: 1040 }}>
      <div className="vp-hello">
        <div>
          <h1>公司</h1>
          <p>
            平台管理員在此建立公司、開通賣房／賣車功能，並可進入公司詳情管理該公司使用者權限。
            此處的平台權限與「公司管理員」分開，不會互相自動取得。
          </p>
        </div>
        <div className="vp-actions">
          <button type="button" className="vp-btn primary" onClick={openCreate}>新增公司</button>
        </div>
      </div>
      <div className="vp-bar">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜尋公司名稱或代碼" aria-label="搜尋公司" />
      </div>
      {error ? (
        <div className="vp-card"><VpState text={errorMessage(error, '無法載入公司清單。')} action={{ label: '重新載入', onClick: reload }} /></div>
      ) : companies.length === 0 ? (
        <div className="vp-card"><VpState text="還沒有公司。" action={{ label: '新增公司', onClick: openCreate }} /></div>
      ) : (
        <div className="vp-list-card">
          <table className="vp-table">
            <thead>
              <tr>
                {['公司', '功能', '成員', '狀態', '操作'].map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr
                  key={c.id}
                  className="vp-row-link"
                  onClick={() => navigate(`/platform/companies/${c.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/platform/companies/${c.id}`); }}
                  tabIndex={0}
                  role="link"
                  aria-label={`開啟 ${c.name} 詳情`}
                >
                  <td>
                    <strong>{c.name}</strong>
                    <div className="vp-muted">{c.short_name}{c.code ? ` · ${c.code}` : ''}</div>
                  </td>
                  <td>
                    <div className="vp-module-badges">
                      {(c.module_labels || []).map((m) => <span key={m.key} className="vp-badge blue">{m.label}</span>)}
                    </div>
                  </td>
                  <td>{c.member_count ?? '—'}</td>
                  <td>{c.is_active === false ? '已停用' : '啟用中'}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="vp-btn" onClick={() => navigate(`/platform/companies/${c.id}`)}>詳情／成員</button>
                    <button type="button" className="vp-btn" onClick={(e) => openEdit(c, e)}>編輯</button>
                    <button type="button" className={`vp-btn${c.is_active === false ? '' : ' danger'}`} onClick={(e) => handleActive(c, e)}>
                      {c.is_active === false ? '啟用' : '停用'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showCreate || edit) && (
        <div className="vp-modal-mask" onClick={(e) => { if (e.target === e.currentTarget) { setShowCreate(false); setEdit(null); } }}>
          <div className="vp-modal" role="dialog" aria-labelledby="company-form-title">
            <div className="vp-modal-head">
              <h2 id="company-form-title">{edit ? '編輯公司與功能' : '新增公司'}</h2>
              <button type="button" className="vp-modal-x" onClick={() => { setShowCreate(false); setEdit(null); }} aria-label="關閉">✕</button>
            </div>
            <form onSubmit={edit ? handleEdit : handleCreate}>
              <label className="vp-label">公司名稱 *
                <input className="vp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={200} />
              </label>
              <label className="vp-label">簡稱
                <input className="vp-input" value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} maxLength={80} />
              </label>
              <label className="vp-label">代碼
                <input className="vp-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} maxLength={40} />
              </label>
              {!edit && (
                <label className="vp-label">產業（選填）
                  <input className="vp-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} maxLength={80} />
                </label>
              )}
              <ModulePicker modules={form.modules} onChange={(modules) => setForm({ ...form, modules })} />
              {!edit && (
                <>
                  <label className="vp-label">首任公司管理員 Email（選填）
                    <input className="vp-input" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
                  </label>
                  <label className="vp-label">管理員姓名（新帳號時建議填）
                    <input className="vp-input" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} maxLength={120} />
                  </label>
                  <label className="vp-label">管理員初始密碼（帳號尚不存在時必填，至少 8 字）
                    <input className="vp-input" type="password" autoComplete="new-password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} minLength={8} />
                  </label>
                  <p className="vp-muted">可一次建立登入帳號並設為公司管理員。Email 已註冊時可只填 Email。</p>
                </>
              )}
              <div className="vp-wiz-actions">
                <button type="button" className="vp-btn" onClick={() => { setShowCreate(false); setEdit(null); }}>取消</button>
                <button type="submit" className="vp-btn primary" disabled={saving}>{saving ? '處理中…' : '確認送出'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
